import React, { useState, useEffect } from 'react';

// === MASSIVE MENU GENERATOR (WITH REAL E-COMMERCE DATA) ===
const generateMenu = () => {
  const baseMenu = {
    "Signature Soups": ["Truffle Mushroom Cream", "Lobster Bisque", "Roasted Pumpkin", "Clear Asparagus", "French Onion", "Tom Yum Goong", "Sweet Corn Puree"],
    "Luxury Starters": ["Avocado Bruschetta", "Gold Leaf Paneer Tikka", "Caviar Blinis", "Truffle Fries", "Stuffed Cremini Mushrooms", "Wagyu Beef Sliders", "Crispy Calamari", "Edamame Sea Salt"],
    "Wood-Fired Pizzas": ["Burrata & Basil", "Truffle & Mushroom", "Prosciutto Arugula", "Quattro Formaggi", "Spicy Pepperoni", "Smoked Salmon Pizza", "Vegan Margherita"],
    "Artisan Pastas": ["Lobster Ravioli", "Black Truffle Linguine", "Seafood Marinara", "Penne Vodka", "Spinach Ricotta Gnocchi", "Aglio Olio Peperoncino", "Wagyu Bolognese"],
    "Indian Fine Dining": ["Dal Bukhara (24hr cooked)", "Paneer Lababdar", "Saffron Malai Kofta", "Dum Aloo Kashmiri", "Nalli Nihari", "Mutton Rogan Josh", "Butter Chicken", "Awadhi Biryani"],
    "Global Mains": ["Grilled Norwegian Salmon", "Herb Crusted Lamb Rack", "Filet Mignon", "Miso Glazed Black Cod", "Ratatouille", "Stuffed Chicken Breast", "Vegan Buddha Bowl"],
    "Decadent Desserts": ["24K Gold Chocolate Dome", "Classic Tiramisu", "Saffron Baked Yogurt", "Matcha Cheesecake", "Vanilla Bean Panna Cotta", "Molten Lava Cake", "Berry Pavlova"],
    "Handcrafted Cocktails": ["Smoked Old Fashioned", "Gold Leaf Martini", "Elderflower Spritz", "Spicy Mezcal Margarita", "Classic Negroni", "Aperol Spritz", "Hibiscus Gin Sour"]
  };

  let idCounter = 1;
  let basePrice = 350;
  return Object.keys(baseMenu).map((cat) => {
    basePrice += 50;
    return {
      category: cat,
      items: baseMenu[cat as keyof typeof baseMenu].map((item, idx) => ({
        id: idCounter++,
        name: item,
        price: basePrice + (idx * 45),
        desc: idx % 3 === 0 ? "Chef's Signature." : "Prepared with imported premium ingredients."
      }))
    };
  });
};

const massiveMenu = generateMenu();

// === COMPONENT ===
const CafePremium: React.FC = () => {
  type Page = 'home' | 'menu' | 'reservation' | 'events' | 'about';
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [isReserved, setIsReserved] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // E-Commerce State
  const [cart, setCart] = useState<{id: number, name: string, price: number, qty: number}[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false); // Close mobile menu on page change
  }, [currentPage]);

  // Cart Functions
  const addToCart = (item: {id: number, name: string, price: number}) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) return prev.map(i => i.id === item.id ? { ...i, qty: i.qty + 1 } : i);
      return [...prev, { ...item, qty: 1 }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (id: number) => {
    setCart(prev => prev.filter(i => i.id !== id));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);

  // WhatsApp Checkout Integration
  const handleWhatsAppCheckout = () => {
    if (cart.length === 0) return;
    let message = "Hello The Grand Onyx, I'd like to place an order:%0a%0a";
    cart.forEach(item => {
      message += `▪ ${item.qty}x ${item.name} (₹${item.price * item.qty})%0a`;
    });
    message += `%0a*Total Payable: ₹${cartTotal}*%0a%0aPlease confirm my order and delivery time.`;
    
    window.open(`https://wa.me/919876543210?text=${message}`, '_blank');
  };

  const handleReservation = (e: React.FormEvent) => {
    e.preventDefault();
    setIsReserved(true);
    setTimeout(() => setIsReserved(false), 5000); 
  };

  return (
    <div className="min-h-screen bg-[#111111] text-zinc-300 font-sans flex flex-col selection:bg-amber-500 selection:text-white overflow-x-hidden">
      
      {/* --- CART DRAWER (E-COMMERCE OVERLAY) --- */}
      {isCartOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsCartOpen(false)}></div>
          <div className="relative w-full sm:w-[400px] max-w-full bg-[#1a1a1a] h-full shadow-2xl flex flex-col border-l border-zinc-800 animate-fadeInRight">
            <div className="p-4 sm:p-6 border-b border-zinc-800 flex justify-between items-center bg-[#111]">
              <h2 className="text-xl sm:text-2xl font-serif text-amber-500">Your Order</h2>
              <button onClick={() => setIsCartOpen(false)} className="text-zinc-500 hover:text-white text-3xl leading-none">×</button>
            </div>
            
            <div className="flex-grow overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 no-scrollbar">
              {cart.length === 0 ? (
                <div className="text-center text-zinc-600 mt-20">
                  <span className="text-5xl block mb-4">🛒</span>
                  <p>Your cart is empty.</p>
                </div>
              ) : (
                cart.map(item => (
                  <div key={item.id} className="flex justify-between items-center bg-[#222] p-4 rounded-xl border border-zinc-800">
                    <div className="pr-2">
                      <h4 className="font-bold text-zinc-100 text-sm sm:text-base">{item.name}</h4>
                      <p className="text-amber-500 text-xs sm:text-sm">₹{item.price} x {item.qty}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="font-bold text-white text-sm sm:text-base">₹{item.price * item.qty}</span>
                      <button onClick={() => removeFromCart(item.id)} className="text-red-500 hover:text-red-400 text-[10px] sm:text-xs border border-red-500/30 px-2 py-1 rounded uppercase tracking-wider">Remove</button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 sm:p-6 border-t border-zinc-800 bg-[#111]">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <span className="text-sm sm:text-base text-zinc-400 uppercase tracking-widest text-xs">Total Amount</span>
                <span className="text-2xl sm:text-3xl font-serif text-amber-500">₹{cartTotal}</span>
              </div>
              <button 
                onClick={handleWhatsAppCheckout}
                disabled={cart.length === 0}
                className={`w-full py-4 uppercase tracking-widest font-bold text-xs sm:text-sm transition ${cart.length === 0 ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed' : 'bg-amber-600 text-[#111] hover:bg-amber-500 shadow-xl shadow-amber-600/20'}`}
              >
                Checkout via WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- LUXURY NAVBAR --- */}
      <nav className="bg-[#111]/95 backdrop-blur-md sticky top-0 z-50 border-b border-zinc-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 md:h-24 flex items-center justify-between">
          
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer group" onClick={() => setCurrentPage('home')}>
             <span className="text-3xl md:text-4xl text-amber-500">♔</span>
             <span className="text-lg sm:text-xl md:text-2xl font-serif tracking-[0.1em] sm:tracking-[0.2em] text-white uppercase">The Grand <span className="font-light text-amber-500">Onyx</span></span>
          </div>
          
          {/* Desktop Links */}
          <div className="hidden md:flex gap-8 lg:gap-10 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">
            {['home', 'menu', 'reservation', 'events', 'about'].map(page => (
              <button 
                key={page} 
                onClick={() => setCurrentPage(page as Page)}
                className={`hover:text-amber-500 transition ${currentPage === page ? 'text-amber-500 border-b border-amber-500 pb-1' : ''}`}
              >
                {page.replace('events', 'private dining')}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4 md:gap-6">
            <button onClick={() => setIsCartOpen(true)} className="relative text-white hover:text-amber-500 transition">
              <span className="text-2xl">🛒</span>
              {cart.length > 0 && <span className="absolute -top-1 -right-2 bg-amber-600 text-[#111] text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full">{cart.reduce((a,b)=>a+b.qty,0)}</span>}
            </button>
            <button onClick={() => setCurrentPage('reservation')} className="border border-amber-600 text-amber-500 px-4 sm:px-6 py-2 sm:py-2.5 text-[10px] sm:text-xs font-bold uppercase tracking-[0.1em] sm:tracking-[0.15em] hover:bg-amber-600 hover:text-[#111] transition duration-500 hidden sm:block">
              Book Table
            </button>
            {/* Mobile Hamburger */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden text-amber-500 text-3xl leading-none">
              ☰
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-20 left-0 w-full bg-[#1a1a1a] border-b border-zinc-800 shadow-2xl flex flex-col p-6 animate-fadeIn">
            {['home', 'menu', 'reservation', 'events', 'about'].map(page => (
              <button 
                key={page} 
                onClick={() => setCurrentPage(page as Page)}
                className={`py-3 text-left text-xs font-bold uppercase tracking-[0.2em] border-b border-zinc-800/50 transition ${currentPage === page ? 'text-amber-500' : 'text-zinc-400 hover:text-white'}`}
              >
                {page.replace('events', 'private dining')}
              </button>
            ))}
            <button onClick={() => setCurrentPage('reservation')} className="mt-6 w-full border border-amber-600 text-amber-500 px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] hover:bg-amber-600 hover:text-[#111] transition">
              Book Table
            </button>
          </div>
        )}
      </nav>

      {/* --- PAGE CONTENT AREA --- */}
      <main className="flex-grow animate-fadeIn">
        
        {/* ================= 1. HOME PAGE ================= */}
        {currentPage === 'home' && (
          <div className="animate-slideUp">
            {/* Cinematic Hero */}
            <header className="relative min-h-[80vh] md:h-[90vh] flex items-center justify-center text-center px-4 sm:px-6 overflow-hidden py-20">
              <div className="absolute inset-0">
                <img src="https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?q=80&w=1600&auto=format&fit=crop" alt="Luxury Dining" className="w-full h-full object-cover opacity-30 scale-105 animate-[pulse_10s_ease-in-out_infinite]"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/50 to-[#111]/80"></div>
              </div>
              <div className="relative z-10 max-w-4xl space-y-6 md:space-y-8 mt-10 md:mt-0">
                <span className="text-amber-500 font-bold tracking-[0.2em] md:tracking-[0.4em] uppercase text-[10px] md:text-xs border-y border-amber-500/30 py-2 inline-block px-4">A Michelin-Star Experience</span>
                <h1 className="text-5xl sm:text-6xl md:text-8xl font-serif text-white leading-tight">Taste The <br/><span className="italic font-light text-amber-500">Unforgettable.</span></h1>
                <p className="text-zinc-400 text-base md:text-lg font-light max-w-2xl mx-auto px-4">Elevate your senses with our globally inspired cuisine, masterful cocktails, and impeccable service.</p>
                <div className="pt-6 md:pt-10 flex flex-col sm:flex-row justify-center gap-4 sm:gap-6">
                  <button onClick={() => setCurrentPage('menu')} className="bg-amber-600 text-[#111] px-8 md:px-10 py-4 font-bold uppercase tracking-[0.15em] md:tracking-[0.2em] hover:bg-amber-500 transition text-[10px] md:text-xs">Order Online</button>
                  <button onClick={() => setCurrentPage('reservation')} className="text-white px-8 md:px-10 py-4 font-bold uppercase tracking-[0.15em] md:tracking-[0.2em] hover:text-amber-500 transition text-[10px] md:text-xs underline underline-offset-8">Reserve Experience</button>
                </div>
              </div>
            </header>

            {/* Signature Dishes Spotlight */}
            <section className="py-20 md:py-32 px-4 sm:px-6 max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 md:mb-16 gap-6">
                <div>
                  <span className="text-amber-600 font-bold uppercase tracking-[0.2em] text-[10px] md:text-xs">Culinary Art</span>
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white mt-2 md:mt-4">Signature Creations</h2>
                </div>
                <button onClick={() => setCurrentPage('menu')} className="text-zinc-400 hover:text-amber-500 uppercase tracking-widest text-[10px] md:text-xs transition">View Full Menu ➔</button>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-10">
                {[
                  { n: "Gold Leaf Wagyu", p: "₹4,500", i: "https://kenh14cdn.com/thumb_w/660/2019/3/27/3-1553679334809642363589.jpg" },
                  { n: "Truffle Lobster", p: "₹3,200", i: "https://images.unsplash.com/photo-1565557623262-b51c2513a641?q=80&w=600" },
                  { n: "Smoked Salmon", p: "₹2,800", i: "https://images.unsplash.com/photo-1485921325833-c519f76c4927?q=80&w=600" }
                ].map((dish, idx) => (
                  <div key={idx} className="group cursor-pointer">
                    <div className="h-[300px] md:h-[450px] overflow-hidden bg-zinc-900 relative rounded-xl md:rounded-none">
                      <img src={dish.i} alt={dish.n} className="w-full h-full object-cover group-hover:scale-110 transition duration-1000 opacity-80 group-hover:opacity-100"/>
                      <div className="absolute top-4 right-4 bg-[#111]/80 backdrop-blur-sm text-amber-500 font-serif px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-lg">{dish.p}</div>
                    </div>
                    <div className="mt-4 md:mt-6 border-l-2 border-amber-600 pl-4">
                      <h3 className="text-xl md:text-2xl font-serif text-white">{dish.n}</h3>
                      <p className="text-zinc-500 text-xs md:text-sm mt-1 md:mt-2">Prepared by our Executive Chef</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Private Dining Teaser */}
            <section className="py-20 md:py-32 px-4 sm:px-6 bg-[#1a1a1a] border-y border-zinc-800">
              <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 md:gap-16 items-center">
                <div className="space-y-6 md:space-y-8 pr-0 md:pr-8 order-2 md:order-1 text-center md:text-left">
                  <h2 className="text-4xl sm:text-5xl font-serif text-white">Exclusive Private Dining</h2>
                  <p className="text-zinc-400 text-base md:text-lg leading-relaxed font-light">Host your most important celebrations in our opulent private dining rooms. Featuring personalized 7-course tasting menus, a dedicated sommelier, and absolute privacy.</p>
                  <button onClick={() => setCurrentPage('events')} className="bg-transparent border border-amber-600 text-amber-500 px-6 md:px-8 py-3 md:py-4 font-bold uppercase tracking-[0.2em] text-[10px] md:text-xs hover:bg-amber-600 hover:text-[#111] transition w-full md:w-auto">Discover Spaces</button>
                </div>
                <div className="h-[300px] md:h-[500px] bg-zinc-900 relative order-1 md:order-2 rounded-2xl md:rounded-none overflow-hidden">
                  <img src="https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=800&auto=format&fit=crop" className="w-full h-full object-cover opacity-70" alt="Private Dining"/>
                  <div className="absolute -left-0 md:-left-12 bottom-0 md:bottom-12 bg-[#111] p-4 md:p-8 border border-zinc-800 w-full md:w-auto flex justify-between md:block">
                    <span className="text-xl md:text-3xl font-serif text-amber-500 block md:mb-2">3 Spaces</span>
                    <span className="text-[10px] md:text-xs uppercase tracking-widest text-zinc-500 flex items-center md:block">Up to 50 Guests</span>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* ================= 2. ONLINE ORDER (E-COMMERCE) MENU ================= */}
        {currentPage === 'menu' && (
          <div className="animate-slideUp bg-[#111] py-16 md:py-24 px-4 sm:px-6 relative">
            <div className="max-w-7xl mx-auto text-center mb-12 md:mb-24">
              <span className="text-amber-600 font-bold uppercase tracking-[0.2em] md:tracking-[0.3em] text-[10px] md:text-xs">Direct Delivery</span>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif text-white mt-4 md:mt-6 mb-4">Order The Masterpieces</h1>
              <p className="text-zinc-500 text-sm md:text-lg px-4">Delivered hot and fresh in luxury packaging. Zero commission fees.</p>
            </div>
            
            <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-10 md:gap-16">
              {/* Sticky Categories (Horizontal on Mobile, Vertical on Desktop) */}
              <div className="lg:w-1/4">
                <div className="relative lg:sticky top-0 lg:top-32 bg-[#1a1a1a] p-4 lg:p-8 border border-zinc-800 rounded-xl lg:rounded-none z-10 overflow-hidden">
                  <h3 className="hidden lg:block font-bold uppercase tracking-[0.2em] text-xs mb-8 text-amber-500">Categories</h3>
                  <ul className="flex flex-row lg:flex-col overflow-x-auto lg:overflow-visible no-scrollbar gap-6 lg:gap-0 lg:space-y-5 text-xs lg:text-sm font-medium pb-2 lg:pb-0">
                    {massiveMenu.map((m, i) => (
                      <li key={i} className="text-zinc-400 hover:text-white transition whitespace-nowrap lg:whitespace-normal">
                        <a href={`#cat-${i}`}>{m.category}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Order Items */}
              <div className="lg:w-3/4 space-y-16 md:space-y-24">
                {massiveMenu.map((section, idx) => (
                  <div key={idx} id={`cat-${idx}`} className="scroll-mt-40 lg:scroll-mt-32">
                    <h2 className="text-3xl md:text-4xl font-serif text-white mb-6 md:mb-10 border-b border-zinc-800 pb-4">{section.category}</h2>
                    <div className="grid sm:grid-cols-2 gap-x-8 md:gap-x-12 gap-y-8 md:gap-y-10">
                      {section.items.map((item) => (
                        <div key={item.id} className="flex justify-between items-start group bg-[#151515] sm:bg-transparent p-4 sm:p-0 rounded-xl sm:rounded-none border border-zinc-800 sm:border-none">
                          <div className="pr-4 flex-grow">
                            <h4 className="text-base md:text-lg font-serif text-zinc-100 group-hover:text-amber-500 transition">{item.name}</h4>
                            <p className="text-[10px] md:text-xs text-zinc-500 mt-1 md:mt-2 font-light line-clamp-2 sm:line-clamp-none">{item.desc}</p>
                            <span className="text-amber-600 mt-2 block font-serif text-sm md:text-base">₹{item.price}</span>
                          </div>
                          <button 
                            onClick={() => addToCart(item)}
                            className="bg-[#1a1a1a] border border-zinc-700 md:border-zinc-800 text-amber-500 px-3 md:px-4 py-2 hover:bg-amber-600 hover:text-[#111] hover:border-amber-600 transition uppercase tracking-widest text-[10px] md:text-xs whitespace-nowrap rounded md:rounded-none flex-shrink-0"
                          >
                            Add +
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ================= 3. RESERVATION PAGE ================= */}
        {currentPage === 'reservation' && (
          <div className="animate-slideUp py-16 md:py-24 px-4 sm:px-6 bg-[url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=1600')] bg-cover bg-fixed bg-center">
            <div className="absolute inset-0 bg-[#111]/85 backdrop-blur-sm z-0"></div>
            <div className="max-w-4xl mx-auto bg-[#1a1a1a] border border-amber-900/30 shadow-2xl relative z-10 flex flex-col md:flex-row rounded-2xl md:rounded-none overflow-hidden">
              <div className="md:w-2/5 bg-amber-900/20 p-8 md:p-12 flex flex-col justify-center border-b md:border-b-0 md:border-r border-amber-900/30 text-center md:text-left">
                <h2 className="text-4xl md:text-5xl font-serif text-amber-500 mb-4 md:mb-6">Your Table Awaits.</h2>
                <p className="text-zinc-400 mb-6 md:mb-8 font-light leading-relaxed text-sm md:text-base">Experience culinary perfection. For reservations of 8 or more, please contact the Maitre D' directly.</p>
                <p className="font-bold tracking-widest uppercase text-[10px] md:text-xs text-amber-600">VIP Concierge: <br/><span className="text-white text-base md:text-lg font-serif mt-1 block">+91 98765 43210</span></p>
              </div>
              <div className="md:w-3/5 p-8 md:p-12 bg-[#111]">
                {isReserved ? (
                  <div className="h-full flex flex-col items-center justify-center text-center space-y-4 md:space-y-6 py-10">
                    <span className="text-5xl text-amber-500">♔</span>
                    <h3 className="text-2xl md:text-3xl font-serif text-white">Reservation Confirmed</h3>
                    <p className="text-zinc-500 font-light text-sm md:text-base">An email confirmation has been sent. We look forward to serving you.</p>
                  </div>
                ) : (
                  <form onSubmit={handleReservation} className="space-y-6 md:space-y-8">
                    <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Guest Name</label>
                        <input required type="text" className="w-full bg-transparent border-b border-zinc-700 py-2 text-white focus:border-amber-500 outline-none transition text-sm"/>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Phone</label>
                        <input required type="tel" className="w-full bg-transparent border-b border-zinc-700 py-2 text-white focus:border-amber-500 outline-none transition text-sm"/>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Date</label>
                        <input required type="date" className="w-full bg-transparent border-b border-zinc-700 py-2 text-white focus:border-amber-500 outline-none transition color-scheme-dark text-sm"/>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Time</label>
                        <input required type="time" className="w-full bg-transparent border-b border-zinc-700 py-2 text-white focus:border-amber-500 outline-none transition color-scheme-dark text-sm"/>
                      </div>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">Party Size</label>
                      <select className="w-full bg-[#111] border-b border-zinc-700 py-2 text-white focus:border-amber-500 outline-none transition text-sm">
                        <option>Table for 2</option><option>Table for 4</option><option>Table for 6</option><option>Large Party (Call Us)</option>
                      </select>
                    </div>
                    <button type="submit" className="w-full bg-amber-600 text-[#111] font-bold uppercase tracking-[0.2em] text-[10px] md:text-xs py-4 md:py-5 hover:bg-amber-500 transition mt-2 md:mt-4 rounded md:rounded-none">Confirm Reservation</button>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ================= 4. PRIVATE EVENTS PAGE (PREMIUM ADDITION) ================= */}
        {currentPage === 'events' && (
          <div className="animate-slideUp py-16 md:py-24 px-4 sm:px-6 max-w-7xl mx-auto">
             <div className="text-center mb-16 md:mb-24">
                <span className="text-amber-500 font-bold uppercase tracking-[0.2em] md:tracking-[0.3em] text-[10px] md:text-xs">Curated Events</span>
                <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif text-white mt-4 mb-4 md:mb-6">Celebrate With Us</h1>
                <p className="text-zinc-400 text-sm md:text-lg max-w-2xl mx-auto font-light px-4">From intimate corporate dinners to lavish birthday celebrations, our dedicated events team will craft an unforgettable experience.</p>
             </div>

             <div className="space-y-20 md:space-y-32">
                <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
                  <div className="h-[250px] sm:h-[300px] md:h-[400px] overflow-hidden rounded-2xl md:rounded-none"><img src="https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?q=80&w=800" className="w-full h-full object-cover" alt="Banquets"/></div>
                  <div className="space-y-4 md:space-y-6 text-center md:text-left">
                    <h2 className="text-3xl md:text-4xl font-serif text-white">The Onyx Hall</h2>
                    <p className="text-amber-500 uppercase tracking-widest text-[10px] md:text-xs font-bold">Capacity: 150 Guests</p>
                    <p className="text-zinc-400 leading-relaxed font-light text-sm md:text-base">Our grand banquet hall features crystal chandeliers, a private bar, and customizable floor plans perfect for wedding receptions and grand galas.</p>
                    <button onClick={() => setCurrentPage('reservation')} className="border-b border-amber-600 text-amber-500 pb-1 uppercase tracking-widest text-[10px] md:text-xs hover:text-white transition mt-2 md:mt-4">Inquire Now</button>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
                  <div className="order-2 md:order-1 space-y-4 md:space-y-6 text-center md:text-left">
                    <h2 className="text-3xl md:text-4xl font-serif text-white">The Wine Cellar</h2>
                    <p className="text-amber-500 uppercase tracking-widest text-[10px] md:text-xs font-bold">Capacity: 20 Guests</p>
                    <p className="text-zinc-400 leading-relaxed font-light text-sm md:text-base">Surrounded by our curated collection of vintage wines, this intimate space is ideal for private tastings, board meetings, and exclusive anniversary dinners.</p>
                    <button onClick={() => setCurrentPage('reservation')} className="border-b border-amber-600 text-amber-500 pb-1 uppercase tracking-widest text-[10px] md:text-xs hover:text-white transition mt-2 md:mt-4">Inquire Now</button>
                  </div>
                  <div className="order-1 md:order-2 h-[250px] sm:h-[300px] md:h-[400px] overflow-hidden rounded-2xl md:rounded-none"><img src="https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?q=80&w=800" className="w-full h-full object-cover" alt="Wine Cellar"/></div>
                </div>
             </div>
          </div>
        )}

      </main>

      {/* ================= ULTRA-LUXURY FOOTER ================= */}
      <div className="w-full grid grid-cols-3 md:grid-cols-6 mt-16 border-y border-zinc-900">
        {[
          '1568901346375-23c9450c58cd', '1504630083234-14187a9df0f5', '1512621776951-a57141f2eefd',
          '1565299624946-b28f40a0ae38', '1473093295043-cdd812d0e601', '1497935586351-b67a49e012bf'
        ].map((id, index) => (
           <a key={index} href="#" className="relative group block aspect-square overflow-hidden bg-black">
              <img src={`https://images.unsplash.com/photo-${id}?q=80&w=400&auto=format&fit=crop`} alt="Gallery" className="w-full h-full object-cover group-hover:opacity-30 transition duration-700 opacity-60"/>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition duration-500 text-amber-500 font-bold tracking-[0.1em] md:tracking-[0.2em] text-[8px] md:text-xs">@THEONYX</div>
           </a>
        ))}
      </div>

      <footer className="w-full bg-[#0a0a0a] font-sans text-zinc-400 py-16 md:py-24 px-6 border-t-[4px] md:border-t-[8px] border-amber-600">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-16 text-center md:text-left">
          
          <div className="lg:col-span-5 space-y-6 md:space-y-8 flex flex-col items-center md:items-start">
            <div className="text-3xl md:text-4xl font-serif text-white uppercase tracking-widest"><span className="text-amber-500">♔</span> The Grand Onyx</div>
            <p className="text-xs md:text-sm font-light leading-relaxed max-w-sm">Award-winning fine dining in the heart of the city. We blend culinary innovation with timeless luxury to create experiences that linger long after the final course.</p>
            <div className="flex gap-4">
               <span className="w-10 h-10 border border-zinc-800 rounded-full flex items-center justify-center hover:bg-amber-600 hover:text-[#111] transition cursor-pointer text-xs">IG</span>
               <span className="w-10 h-10 border border-zinc-800 rounded-full flex items-center justify-center hover:bg-amber-600 hover:text-[#111] transition cursor-pointer text-xs">FB</span>
            </div>
          </div>
          
          <div className="lg:col-span-3 space-y-4 md:space-y-6">
             <h4 className="font-bold text-white uppercase tracking-[0.2em] text-[10px] md:text-xs">Information</h4>
             <ul className="space-y-3 md:space-y-4 text-xs md:text-sm font-light">
                <li><button onClick={() => setCurrentPage('home')} className="hover:text-amber-500 transition">Our Story</button></li>
                <li><button onClick={() => setCurrentPage('menu')} className="hover:text-amber-500 transition">Order Online</button></li>
                <li><button onClick={() => setCurrentPage('reservation')} className="hover:text-amber-500 transition">Reservations</button></li>
                <li><button onClick={() => setCurrentPage('events')} className="hover:text-amber-500 transition">Private Events</button></li>
             </ul>
          </div>

          <div className="lg:col-span-4 space-y-4 md:space-y-6 flex flex-col items-center md:items-start">
             <h4 className="font-bold text-white uppercase tracking-[0.2em] text-[10px] md:text-xs">The Estate</h4>
             <div className="space-y-3 md:space-y-4 text-xs md:text-sm font-light">
                <p className="flex justify-center md:justify-start gap-3 md:gap-4"><span className="text-amber-500">📍</span> 101, Luxury Boulevard, New Delhi</p>
                <p className="flex justify-center md:justify-start gap-3 md:gap-4"><span className="text-amber-500">📞</span> VIP Concierge: +91 98765 43210</p>
                <p className="flex justify-center md:justify-start gap-3 md:gap-4"><span className="text-amber-500">🕒</span> Mon-Sun | 7:00 PM to 1:00 AM</p>
                <p className="flex justify-center md:justify-start gap-3 md:gap-4"><span className="text-amber-500">👔</span> Dress Code: Smart Elegant</p>
             </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto border-t border-zinc-900 mt-12 md:mt-20 pt-6 md:pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-[8px] md:text-[10px] font-bold tracking-[0.1em] md:tracking-[0.2em] uppercase text-zinc-600 text-center md:text-left">
           <p>© {new Date().getFullYear()} The Grand Onyx. All Rights Reserved.</p>
           <p>Developed for Premium Portfolio Demos</p>
        </div>
      </footer>

      {/* Animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fadeInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
        .animate-slideUp { animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        .animate-fadeInRight { animation: fadeInRight 0.4s ease-out forwards; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .color-scheme-dark { color-scheme: dark; }
      `}} />
    </div>
  );
};

export default CafePremium;